import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import moment from "moment";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Safely return a trimmed summary string or empty string
export const summaryText = (raw: unknown) =>
  typeof raw === "string" && raw.trim().length ? raw : "";

// Utility function to parse JSON string, handling markdown code blocks
export const parseJsonStr = (jsonString: string): unknown | null => {
  if (!jsonString) return null;
  try {
    // First try parsing as-is
    return JSON.parse(jsonString);
  } catch {
    try {
      // Try removing code fences if present
      const cleanedString = jsonString
        .replace(/```json\s*/g, "")
        .replace(/```\s*/g, "")
        .trim();
      return JSON.parse(cleanedString);
    } catch {
      // console.warn("Unable to parse JSON string:", jsonString, secondError);
      return null;
    }
  }
};

// Format duration in seconds to MM:SS format
export const formatVideoDuration = (durationSeconds: number): string => {
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = Math.floor(durationSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Parse Nomadic-style timestamps ("t=44", "t=00:22", "44s", "90.5") into seconds
export const parseNomadicTimestampToSeconds = (input: string | number | null | undefined): number => {
  if (input == null) return 0;
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : 0;
  }

  const raw = String(input).trim();
  if (!raw) return 0;

  const candidate = raw.replace(/^t=/i, '').replace(/,/g, '.').trim();
  if (!candidate) return 0;

  const [head] = candidate.split(/[\-–—]/, 1);
  const timePart = head?.trim() || candidate;

  if (timePart.includes(':')) {
    const parts = timePart.split(':');
    let total = 0;
    let multiplier = 1;
    for (let i = parts.length - 1; i >= 0; i--) {
      const cleaned = parts[i].replace(/[^0-9.]+/g, '');
      if (!cleaned) {
        multiplier *= 60;
        continue;
      }
      const value = parseFloat(cleaned);
      if (!Number.isNaN(value)) {
        total += value * multiplier;
      }
      multiplier *= 60;
    }
    if (Number.isFinite(total)) {
      return total;
    }
  }

  const numeric = parseFloat(timePart.replace(/[^0-9.]+/g, ''));
  return Number.isNaN(numeric) ? 0 : numeric;
};

// Backward compatibility with older helpers
export const getTimeInSeconds = (timeStr: string): number => parseNomadicTimestampToSeconds(timeStr);

export const coerceFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  return null;
};

// Get Tailwind CSS background color class for severity levels
// Reusable across components for consistent severity color coding
export const getSeverityColor = (severity: string): string => {
  switch (severity?.toLowerCase()) {
    case "high":
      return "bg-red-500";
    case "medium":
      return "bg-amber-500";
    case "low":
      return "bg-blue-400";
    default:
      return "bg-gray-500";
  }
};

// Determine whether a final decision string represents a
// conclusive ACCEPTED/REJECTED status or a pending state.
export const getFinalDecisionStatus = (
  decision?: string
): "ACCEPTED" | "REJECTED" | "PENDING" => {
  if (!decision) return "PENDING";
  const cleaned = decision
    .replace(/\(.*?\)/, "") // drop parenthetical notes
    .split(/[–-]/)[0] // drop reason after dash/en dash
    .trim()
    .toUpperCase();
  if (cleaned === "ACCEPT" || cleaned === "ACCEPTED") return "ACCEPTED";
  if (cleaned === "REJECT" || cleaned === "REJECTED") return "REJECTED";
  return "PENDING";
};

// Map backend AnalysisTypes enum values to frontend TAG_CONFIGS keys
const ANALYSIS_TYPE_TO_TAG_CONFIG: Record<string, string> = {
  edge_case: "Edge Case Detection",
  violation: "default",
  rapid_review: "rapid_review",
  rapid: "rapid_review", // Handle "rapid" as "rapid_review"
  long_search: "default",
};

export const mapAnalysisTypeToTagConfig = (analysisType?: string): string => {
  if (!analysisType) return "default";
  return (
    ANALYSIS_TYPE_TO_TAG_CONFIG[analysisType.toLowerCase()] ?? analysisType
  );
};

// Get effective tag using the hierarchy system with backward compatibility
export const getEffectiveTag = (
  video: any,
  analysis?: any,
  analysisId?: string
): string => {
  // Check if this is a livestream session first
  if (video?.is_livestream || video?.source === 'livestream') {
    return "livestream";
  }
  
  // If analysis_id exists (Rapid Review workflow)
  if (analysisId && analysis?.analysis_type) {
    // Check if this is a driving footage filter analysis by looking at the query
    if (analysis?.query && analysis.query.includes("DRIVING FOOTAGE FILTER ANALYSIS")) {
      return "Driving Footage Filter";
    }
    return mapAnalysisTypeToTagConfig(analysis.analysis_type);
  }
  
  // Edge Case Detection workflow - check visual_analysis.analysis_type first
  if (video?.visual_analysis?.analysis_type) {
    return mapAnalysisTypeToTagConfig(video.visual_analysis.analysis_type);
  }
  
  // Special backward compatibility for old edge case videos
  if (video?.tag === "Edge Case Detection") {
    return video.tag;
  }
  
  // Note: no code path sets analysis_type to "driving_footage_filter"; rely on analysis.query detection instead
  
  // General fallback to root tag
  if (video?.tag) {
    return video.tag;
  }
  
  // Final fallback
  return "default";
};

// Time helpers: normalize mixed Firestore/JS timestamp shapes and format consistently
export const toEpochSeconds = (ts: any): number | 0 => {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === 'function') return Math.floor(ts.toMillis() / 1000);
    if (typeof ts?.toDate === 'function') return Math.floor(ts.toDate().getTime() / 1000);
    if (typeof ts?.seconds === 'number') return Math.floor(ts.seconds);
    if (ts instanceof Date) return Math.floor(ts.getTime() / 1000);
    if (typeof ts === 'number') return ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(ts);
    if (typeof ts === 'string') {
      const parsed = Date.parse(ts);
      if (!isNaN(parsed)) return Math.floor(parsed / 1000);
    }
  } catch (e) {
    console.error("toEpochSeconds error:", e);
  }
  return 0;
};

export const formatCreatedAt = (ts: any, fmt = 'MMM DD, YYYY'): string => {
  const s = toEpochSeconds(ts);
  return s ? moment.unix(s).format(fmt) : 'Unknown date';
};

export const formatRelativeTime = (ts: any): string => {
  const s = toEpochSeconds(ts);
  if (!s) return 'Unknown';
  return moment.unix(s).fromNow();
};
